import { useState, useRef, useEffect, useCallback } from "react";

/* Tela de verificação de e-mail por código: 6 caixinhas de dígito (o
   backend gera código de 6 dígitos). Ao completar, dispara a verificação
   sozinho (sem botão). Feedback simples: pisca/balança vermelho se errar
   (e limpa pra tentar de novo), ou pulsa verde com um ✓ se acertar (avança
   sozinho depois de alguns segundos, ou o usuário pula na hora). */

const N = 6;
const RESEND_SECONDS = 24;

// canal "whatsapp": o mesmo fluxo, com os textos do código que chega pelo WhatsApp.
// Sem onAdvance (dois códigos na mesma tela), quem avança é a tela de fora;
// jaConfirmado: voltou para a tela depois de ter confirmado este código.
export function CadastroOtpAnim({ email, onVerify, onResend, onAdvance, canal = "email", autoFocus = true, jaConfirmado = false }) {
  const zap = canal === "whatsapp";
  const [digits, setDigits]   = useState(Array(N).fill(""));
  const [fase, setFase]       = useState(jaConfirmado ? "confirmado" : "digitando"); // digitando | verificando | erro | confirmado
  const [erro, setErro]       = useState("");
  const [resendIn, setResendIn] = useState(RESEND_SECONDS);
  const [reenviando, setReenviando] = useState(false);
  const [reenviouOk, setReenviouOk] = useState(false);

  const inputsRef = useRef([]);

  useEffect(() => { if (autoFocus) inputsRef.current[0]?.focus(); }, [autoFocus]);

  // Contagem pro "reenviar código"
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const focar = (i) => inputsRef.current[i]?.focus();

  const setDigito = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    setDigits(arr => {
      const novo = [...arr]; novo[i] = d; return novo;
    });
    if (d && i < N - 1) focar(i + 1);
  };

  const onKeyDown = (i, e) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) { focar(i - 1); }
    if (e.key === "ArrowLeft" && i > 0) focar(i - 1);
    if (e.key === "ArrowRight" && i < N - 1) focar(i + 1);
  };

  const onPaste = (e) => {
    const texto = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, N);
    if (!texto) return;
    e.preventDefault();
    setDigits(arr => {
      const novo = [...arr];
      for (let i = 0; i < N; i++) novo[i] = texto[i] || novo[i] || "";
      return novo;
    });
    focar(Math.min(texto.length, N - 1));
  };

  const verificar = useCallback(async (codigo) => {
    setFase("verificando"); setErro("");
    try {
      await onVerify(codigo);
      setFase("confirmado");
      if (onAdvance) setTimeout(() => onAdvance(), 2200);
    } catch (e) {
      setErro(e?.message || "Código incorreto.");
      setFase("erro");
      setTimeout(() => {
        setDigits(Array(N).fill(""));
        setFase("digitando");
        focar(0);
      }, 900);
    }
  }, [onVerify, onAdvance]);

  // Dispara sozinho quando os 6 dígitos estiverem preenchidos.
  useEffect(() => {
    if (fase === "digitando" && digits.every(d => d !== "")) {
      verificar(digits.join(""));
    }
  }, [digits, fase, verificar]);

  const reenviar = async () => {
    setReenviando(true); setReenviouOk(false); setErro("");
    try {
      await onResend();
      setResendIn(RESEND_SECONDS);
      setReenviouOk(true);
      setTimeout(() => setReenviouOk(false), 4000);
    } catch (e) { setErro(e?.message || "Não foi possível reenviar."); }
    setReenviando(false);
  };

  const pular = () => onAdvance();

  return (
    <div>
      <style>{`
        .otp-stage{ display:flex; justify-content:center; gap:10px; padding:22px 0 8px; }
        .otp-box input{
          width:44px; height:54px; text-align:center; font-size:22px; font-weight:800;
          font-family:'JetBrains Mono',monospace; color:#f1f5f9; background:rgba(255,255,255,0.04);
          border:1.5px solid rgba(255,255,255,0.12); border-radius:10px; outline:none;
          transition:border-color .2s, box-shadow .2s, background .2s, opacity .2s;
        }
        .otp-box input:focus{ border-color:#00d4aa; box-shadow:0 0 0 3px rgba(0,212,170,0.15); }
        .otp-stage.verificando .otp-box input{ opacity:.55; }
        .otp-stage.confirmado .otp-box input{
          border-color:#22ff9c; background:rgba(34,255,156,0.1); box-shadow:0 0 14px rgba(34,255,156,0.5);
          animation: otpPop .45s ease both;
        }
        .otp-stage.confirmado .otp-box:nth-child(1) input{ animation-delay:.00s }
        .otp-stage.confirmado .otp-box:nth-child(2) input{ animation-delay:.05s }
        .otp-stage.confirmado .otp-box:nth-child(3) input{ animation-delay:.10s }
        .otp-stage.confirmado .otp-box:nth-child(4) input{ animation-delay:.15s }
        .otp-stage.confirmado .otp-box:nth-child(5) input{ animation-delay:.20s }
        .otp-stage.confirmado .otp-box:nth-child(6) input{ animation-delay:.25s }
        @keyframes otpPop{ 0%{transform:scale(1)} 45%{transform:scale(1.18)} 100%{transform:scale(1)} }
        .otp-stage.erro .otp-box input{ border-color:#ef4444; background:rgba(239,68,68,0.1); animation:otpShake .4s ease; }
        @keyframes otpShake{ 0%,100%{transform:translateX(0)} 20%{transform:translateX(-7px)} 40%{transform:translateX(6px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(3px)} }
        .otp-check{ width:52px; height:52px; margin:6px auto 0; border-radius:50%; background:rgba(34,255,156,0.12); border:2px solid #22ff9c;
          display:flex; align-items:center; justify-content:center; font-size:26px; color:#22ff9c;
          animation: otpCheckIn .5s cubic-bezier(.34,1.56,.64,1) both; box-shadow:0 0 24px rgba(34,255,156,0.4); }
        @keyframes otpCheckIn{ from{ transform:scale(0) rotate(-25deg); opacity:0 } to{ transform:scale(1) rotate(0deg); opacity:1 } }
        .otp-status{ text-align:center; font-size:12.5px; color:#94a3b8; margin-top:10px; min-height:18px; }
        .otp-resend{ text-align:center; margin-top:16px; font-size:11.5px; color:#475569; font-family:'JetBrains Mono',monospace; }
        .otp-resend button{ background:none; border:none; color:#00d4aa; cursor:pointer; font-weight:700; font-family:'JetBrains Mono',monospace; font-size:11.5px; padding:0; }
        .otp-resend button:disabled{ color:#334155; cursor:default; }
        .otp-skip{ display:block; margin:14px auto 0; background:none; border:none; color:#00d4aa; cursor:pointer; font-size:11.5px; font-weight:700; font-family:'JetBrains Mono',monospace; }
      `}</style>

      <div style={{display:"flex",alignItems:"flex-start",gap:10,background:"rgba(0,212,170,0.06)",border:"1px solid rgba(0,212,170,0.2)",borderRadius:10,padding:"10px 14px",marginBottom:6,fontSize:12,color:"#94a3b8"}}>
        <span style={{fontSize:16,flexShrink:0,lineHeight:"18px"}}>{zap ? "💬" : "✉️"}</span>
        <span style={{minWidth:0,overflowWrap:"anywhere",lineHeight:1.5}}>
          {jaConfirmado ? <>{zap ? "WhatsApp" : "E-mail"} <strong style={{color:"#f1f5f9"}}>{email}</strong> confirmado.</>
            : <>Enviamos um código de 6 dígitos {zap ? "pelo WhatsApp para" : "pra"} <strong style={{color:"#f1f5f9"}}>{email}</strong>.</>}
        </span>
      </div>
      {fase !== "confirmado" && <div className="otp-help" style={{textAlign:"center",fontSize:11,lineHeight:1.6,marginTop:6}}>
        {zap ? <>Não chegou? Confira se o número tem <strong style={{color:"#94a3b8"}}>WhatsApp</strong> e aguarde alguns segundos.</>
          : <>Não achou o e-mail? Confira também a caixa de <strong style={{color:"#94a3b8"}}>spam/lixo eletrônico</strong>.</>}
      </div>}

      <div className={`otp-stage ${fase}`}>
        {digits.map((d, i) => (
          <div key={i} className="otp-box">
            <input
              ref={el => inputsRef.current[i] = el}
              inputMode="numeric"
              aria-label={`${zap ? "WhatsApp" : "E-mail"}: dígito ${i + 1} de ${N}`}
              maxLength={1}
              autoComplete={i === 0 ? "one-time-code" : "off"}
              value={fase === "confirmado" ? "" : d}
              disabled={fase === "verificando" || fase === "confirmado"}
              onChange={e => setDigito(i, e.target.value)}
              onKeyDown={e => onKeyDown(i, e)}
              onPaste={onPaste}
            />
          </div>
        ))}
      </div>

      {fase === "confirmado" && <div className="otp-check">✓</div>}

      <div className="otp-status" role="status">
        {fase === "verificando" && "Verificando código..."}
        {fase === "confirmado" && (zap ? "WhatsApp confirmado!" : "E-mail confirmado!")}
        {fase === "erro" && <span style={{color:"#fca5a5"}}>{erro || "Código incorreto — tente de novo."}</span>}
      </div>

      {fase === "confirmado" && onAdvance && (
        <button className="otp-skip" onClick={pular}>Pular →</button>
      )}

      {(fase === "digitando" || fase === "erro") && (
        <div className="otp-resend">
          {reenviouOk ? (
            <span style={{color:"#00d4aa"}}>✓ Código reenviado!</span>
          ) : resendIn > 0 ? (
            <>Não recebeu o código? Reenviar em {resendIn}s</>
          ) : (
            <>Não recebeu o código? <button onClick={reenviar} disabled={reenviando}>{reenviando ? "Enviando..." : "Reenviar código"}</button></>
          )}
        </div>
      )}
    </div>
  );
}
